import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AMB, accents, isAmbianceSetting, isAmbiancePeriod, resolveAmbiance } from '../../lib/ambiances';
import type { AccentTokens, AmbianceKey, AmbiancePeriod, AmbianceSetting, ThemeMode } from '../../lib/ambiances';

/**
 * Ambiance de la plateforme : un seul réglage, choisi par le super admin, appliqué à tout le
 * monde (vitrine, connexion, bande d'en-tête de chaque établissement). Lu sans être connecté
 * via /api/auth/global-settings (clé `ambiance`, liste blanche publique côté API).
 */
interface AmbianceContextValue {
    /** Le réglage brut (peut être « auto »). */
    setting: AmbianceSetting;
    /** L'ambiance effectivement affichée après résolution de « auto ». */
    active: AmbianceKey;
    /** Thème courant, suivi depuis html[data-theme]. */
    mode: ThemeMode;
    /** Jetons d'accent de l'ambiance active pour le thème courant. */
    tokens: AccentTokens;
    /** Calendrier des périodes (réglé par le super admin) ; il prime sur le réglage de base. */
    schedule: AmbiancePeriod[];
    /** Après une sauvegarde super admin : applique tout de suite sans recharger. */
    setSetting: (s: AmbianceSetting) => void;
    setSchedule: (p: AmbiancePeriod[]) => void;
    loaded: boolean;
}

const AmbianceContext = createContext<AmbianceContextValue | undefined>(undefined);

const readMode = (): ThemeMode =>
    typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'light' ? 'L' : 'D';

export function AmbianceProvider({ children }: { children: React.ReactNode }) {
    const [setting, setSettingState] = useState<AmbianceSetting>('auto');
    const [schedule, setScheduleState] = useState<AmbiancePeriod[]>([]);
    const [loaded, setLoaded] = useState(false);
    const [mode, setMode] = useState<ThemeMode>(readMode);

    useEffect(() => {
        let cancelled = false;
        fetch('/api/auth/global-settings')
            .then((r) => (r.ok ? r.json() : {}))
            .then((data: any) => {
                if (cancelled) return;
                if (isAmbianceSetting(data?.ambiance)) setSettingState(data.ambiance);
                if (Array.isArray(data?.ambianceSchedule)) setScheduleState(data.ambianceSchedule.filter(isAmbiancePeriod));
            })
            .catch(() => { /* hors ligne : on reste sur « auto » */ })
            .finally(() => { if (!cancelled) setLoaded(true); });
        return () => { cancelled = true; };
    }, []);

    // Le thème est posé sur <html> par useTheme (et forcé en clair par certaines pages publiques).
    useEffect(() => {
        const root = document.documentElement;
        const obs = new MutationObserver(() => setMode(readMode()));
        obs.observe(root, { attributes: true, attributeFilter: ['data-theme'] });
        setMode(readMode());
        return () => obs.disconnect();
    }, []);

    const setSetting = useCallback((s: AmbianceSetting) => setSettingState(s), []);
    const setSchedule = useCallback((p: AmbiancePeriod[]) => setScheduleState(p), []);
    // Réévalué à l'ouverture et une fois par heure : une période qui commence à minuit s'applique sans recharger.
    const [tick, setTick] = useState(0);
    useEffect(() => { const t = window.setInterval(() => setTick((n) => n + 1), 60 * 60 * 1000); return () => window.clearInterval(t); }, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const active = useMemo(() => resolveAmbiance(setting, new Date(), schedule), [setting, schedule, tick]);
    const tokens = useMemo(() => accents(active, mode), [active, mode]);

    const value = useMemo<AmbianceContextValue>(
        () => ({ setting, active, mode, tokens, schedule, setSetting, setSchedule, loaded }),
        [setting, active, mode, tokens, schedule, setSetting, setSchedule, loaded],
    );
    return <AmbianceContext.Provider value={value}>{children}</AmbianceContext.Provider>;
}

export function useAmbiance(): AmbianceContextValue {
    const ctx = useContext(AmbianceContext);
    if (!ctx) throw new Error('useAmbiance doit être utilisé sous AmbianceProvider');
    return ctx;
}

// Texte lisible sur l'accent plein : même règle de luminance que AuthContext pour --brand-fg.
const onAccent = (hex: string): string => {
    const h = hex.replace('#', '');
    const r = parseInt(h.substring(0, 2), 16) || 0;
    const g = parseInt(h.substring(2, 4), 16) || 0;
    const b = parseInt(h.substring(4, 6), 16) || 0;
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.5 ? '#000000' : '#ffffff';
};

const ACCENT_VARS = ['--acc', '--acc-on', '--acc-bg', '--acc-bd', '--acc-fg'] as const;

/**
 * Recolore l'accent de l'app connectée. Une ambiance de fête remplace la couleur de
 * l'établissement ; Jour / Nuit et Maison la laissent (chaque client garde sa couleur).
 * À monter une fois dans la coquille connectée — jamais dans les widgets embarqués, qui
 * portent la marque du client sur son propre site.
 */
export const AmbianceAccent: React.FC = () => {
    const { active, tokens } = useAmbiance();
    useEffect(() => {
        const root = document.documentElement.style;
        if (AMB[active].festive) {
            root.setProperty('--acc', tokens.acc);
            root.setProperty('--acc-on', onAccent(tokens.acc));
            root.setProperty('--acc-bg', tokens.bg);
            root.setProperty('--acc-bd', tokens.bd);
            root.setProperty('--acc-fg', tokens.fg);
        } else {
            ACCENT_VARS.forEach((v) => root.removeProperty(v));
        }
        return () => { ACCENT_VARS.forEach((v) => root.removeProperty(v)); };
    }, [active, tokens]);
    return null;
};
